import axiosInstance from './axios';
import { pathLooksPdf } from './storageUrl';

export const mimeTypeIsPdf = (mime) => String(mime || '').toLowerCase().includes('application/pdf');

function storageRelativePath(stored) {
  const raw = String(stored || '').trim().replace(/\\/g, '/').replace(/^\/+/, '');
  if (!raw) return null;
  if (raw.startsWith('storage/')) return raw.slice('storage/'.length);
  if (raw.startsWith('api/storage/')) return raw.slice('api/storage/'.length);
  return raw;
}

export function openBlobUrlInNewTab(url) {
  const link = document.createElement('a');
  link.href = url;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

async function blobPreviewFromResponse(response, fallbackMessage) {
  if (response.status >= 400) {
    throw new Error(fallbackMessage);
  }
  const contentType = response.headers['content-type'] || '';
  if (contentType.includes('application/json') || contentType.includes('text/html')) {
    throw new Error(await parseBlobErrorMessage(response.data, fallbackMessage));
  }
  const mime = contentType || 'application/octet-stream';
  return {
    url: URL.createObjectURL(new Blob([response.data], { type: mime })),
    mimeType: mime,
    isPdf: mimeTypeIsPdf(mime),
  };
}

async function fetchStorageFilePreviewBlob(storedPath) {
  const relPath = storageRelativePath(storedPath);
  if (!relPath) {
    throw new Error('Partner document not found on server');
  }
  try {
    const response = await axiosInstance.get(`/storage/${relPath}`, { responseType: 'blob' });
    return blobPreviewFromResponse(response, 'Partner document not found on server');
  } catch (error) {
    if (error.response?.data instanceof Blob) {
      throw new Error(await parseBlobErrorMessage(
        error.response.data,
        'Partner document not found on server',
      ));
    }
    throw error;
  }
}

async function fetchPartnerDocumentViaApi(partnerId, type) {
  const response = await axiosInstance.get(`/partners/${partnerId}/documents/${type}`, {
    responseType: 'blob',
    params: { inline: 1 },
  });
  return blobPreviewFromResponse(response, 'Partner document not found on server');
}

function shouldFallbackToStoragePath(error) {
  const status = error.response?.status;
  const message = String(error.message || '').toLowerCase();
  return status === 404
    || message.includes('could not be found')
    || message.includes('not found');
}

/** iframe/embed src — PDF viewer hash breaks blob: URLs */
export function buildPdfEmbedSrc(url) {
  const raw = String(url || '').trim();
  if (!raw) return '';
  if (raw.startsWith('blob:') || raw.startsWith('data:')) return raw;
  return `${raw}#toolbar=0&navpanes=0&view=FitH`;
}

async function parseBlobErrorMessage(blob, fallback) {
  try {
    const text = await blob.text();
    const parsed = JSON.parse(text);
    return parsed?.message || parsed?.error || fallback;
  } catch {
    return fallback;
  }
}

function filenameFromContentDisposition(contentDisposition, fallback) {
  if (!contentDisposition) return fallback;
  const match = contentDisposition.match(/filename="(.+)"/);
  return match ? match[1] : fallback;
}

function triggerBrowserDownload(blob, filename, mimeType) {
  const url = URL.createObjectURL(new Blob([blob], { type: mimeType }));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export async function downloadDocumentFile(documentId, documentName) {
  try {
    const response = await axiosInstance.get(`/documents/${documentId}/download`, {
      responseType: 'blob',
    });
    const contentType = response.headers['content-type'] || '';
    if (contentType.includes('application/json')) {
      throw new Error(await parseBlobErrorMessage(response.data, 'Document file not found on server'));
    }
    const filename = filenameFromContentDisposition(
      response.headers['content-disposition'],
      `${documentName || 'document'}.pdf`
    );
    triggerBrowserDownload(response.data, filename, contentType || 'application/pdf');
  } catch (error) {
    if (error.response?.data instanceof Blob) {
      throw new Error(await parseBlobErrorMessage(
        error.response.data,
        'Failed to download document. The PDF file may be missing from the server — try re-uploading it.'
      ));
    }
    throw error;
  }
}

export const receiptPathIsPdf = (claim, type) => {
  const path = type === 'purchase'
    ? (claim?.purchase_receipt_path || claim?.receipt_pdf || '')
    : (claim?.reimbursement_receipt_path || '');
  return pathLooksPdf(path);
};

/**
 * Fetch a protected file via an authenticated API download route and return a
 * blob: URL suitable for iframe/img preview (works when public /storage/ 404s).
 */
export async function fetchDocumentPreviewBlob(documentId) {
  const response = await axiosInstance.get(`/documents/${documentId}/download`, {
    responseType: 'blob',
  });
  return blobPreviewFromResponse(response, 'Document file not found on server');
}

export async function openDocumentInNewTab(documentId) {
  const { url } = await fetchDocumentPreviewBlob(documentId);
  openBlobUrlInNewTab(url);
  setTimeout(() => URL.revokeObjectURL(url), 120_000);
}

export async function fetchPartnerDocumentPreviewBlob(partnerId, type, storedPath = null) {
  try {
    if (partnerId && type) {
      try {
        return await fetchPartnerDocumentViaApi(partnerId, type);
      } catch (error) {
        if (storedPath && shouldFallbackToStoragePath(error)) {
          return fetchStorageFilePreviewBlob(storedPath);
        }
        if (error.response?.data instanceof Blob) {
          throw new Error(await parseBlobErrorMessage(
            error.response.data,
            'Partner document not found on server',
          ));
        }
        throw error;
      }
    }
    if (storedPath) {
      return fetchStorageFilePreviewBlob(storedPath);
    }
    throw new Error('Partner document not found on server');
  } catch (error) {
    if (error.response?.data instanceof Blob) {
      throw new Error(await parseBlobErrorMessage(
        error.response.data,
        'Partner document not found on server',
      ));
    }
    throw error;
  }
}

export async function openPartnerDocumentInNewTab(partnerId, type, storedPath = null) {
  const { url } = await fetchPartnerDocumentPreviewBlob(partnerId, type, storedPath);
  openBlobUrlInNewTab(url);
  setTimeout(() => URL.revokeObjectURL(url), 120_000);
}

export async function fetchClaimReceiptPreviewBlob(claimId, type) {
  const response = await axiosInstance.get(`/claims/${claimId}/receipt/${type}`, {
    responseType: 'blob',
    params: { inline: 1 },
  });
  const contentType = response.headers['content-type'] || '';
  if (contentType.includes('application/json')) {
    throw new Error('Receipt file not found on server');
  }
  const mime = contentType || 'application/octet-stream';
  return {
    url: URL.createObjectURL(new Blob([response.data], { type: mime })),
    mimeType: mime,
    isPdf: mimeTypeIsPdf(mime),
  };
}

export const claimHasPurchaseReceipt = (claim) => Boolean(
  claim?.purchase_receipt_preview_url
  || claim?.purchase_receipt_path
  || claim?.purchase_receipt_url
  || claim?.receipt_url
  || claim?.receipt_pdf
);

export const claimHasReimbursementReceipt = (claim) => Boolean(
  claim?.reimbursement_receipt_preview_url
  || claim?.reimbursement_receipt_path
  || claim?.reimbursement_receipt_url
  || claim?.payment_receipt_url
);

export async function fetchAnalysisPreviewBlob(analysisId) {
  const response = await axiosInstance.get(`/analyses/${analysisId}/download`, {
    responseType: 'blob',
  });
  if (response.status >= 400) {
    throw new Error('Analysis file not found on server');
  }
  const contentType = response.headers['content-type'] || '';
  if (contentType.includes('application/json') || contentType.includes('text/html')) {
    throw new Error('Analysis file not found on server');
  }
  const mime = contentType || 'application/octet-stream';
  return {
    url: URL.createObjectURL(new Blob([response.data], { type: mime })),
    mimeType: mime,
    isPdf: mimeTypeIsPdf(mime),
  };
}

export async function downloadAnalysisDocument(analysisId, fallbackFilename) {
  const response = await axiosInstance.get(`/analyses/${analysisId}/download`, {
    responseType: 'blob',
  });
  if (response.status >= 400) {
    throw new Error('Analysis file not found on server');
  }
  const contentType = response.headers['content-type'] || '';
  if (contentType.includes('application/json') || contentType.includes('text/html')) {
    throw new Error('Analysis file not found on server');
  }
  const filename = filenameFromContentDisposition(
    response.headers['content-disposition'],
    fallbackFilename,
  );
  const url = URL.createObjectURL(new Blob([response.data], { type: contentType || 'application/octet-stream' }));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function openAnalysisDocumentInNewTab(analysisId) {
  const { url } = await fetchAnalysisPreviewBlob(analysisId);
  openBlobUrlInNewTab(url);
  setTimeout(() => URL.revokeObjectURL(url), 120_000);
}

